import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '@/contexts/DatabaseContext';
import DashboardLayout from '@/components/feature/DashboardLayout';

export default function ProfilePage() {
  const { t } = useTranslation('common');
  const { currentUser, updateProfile } = useDatabase();

  const [fullName, setFullName] = useState(currentUser.fullName);
  const [email, setEmail] = useState(currentUser.email);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!currentPassword) {
      setMessage({ type: 'error', text: t('currentPasswordRequired') });
      return;
    }
    if (newPassword && newPassword !== confirmNewPassword) {
      setMessage({ type: 'error', text: t('passwordsDontMatch') });
      return;
    }

    setSaving(true);
    const result = await updateProfile({
      currentPassword,
      fullName: fullName.trim(),
      email: email.trim(),
      ...(newPassword ? { newPassword } : {}),
    });
    setSaving(false);

    if (result.success) {
      setMessage({ type: 'success', text: t('profileUpdated') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto">
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
          {t('myProfile')}
        </h1>

        <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 font-semibold text-lg shrink-0">
              {currentUser.fullName.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-900">{currentUser.fullName}</p>
              <p className="text-xs text-foreground-500">{t(currentUser.role)}</p>
            </div>
          </div>

          {message && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                message.type === 'success' ? 'bg-primary-50 text-primary-800' : 'bg-accent-50 text-accent-800'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider">
              {t('profileInfo')}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-foreground-600 mb-1.5 block">{t('fullName')}</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground-600 mb-1.5 block">{t('email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            <div className="border-t border-background-200 pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider">
                  {t('password')}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  <i className={`${showPasswords ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`} />
                  {showPasswords ? t('close') : t('view')}
                </button>
              </div>

              <div>
                <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                  {t('currentPassword')}
                </label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                    {t('newPassword')}
                  </label>
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                    {t('confirmNewPassword')}
                  </label>
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={!newPassword}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
              <p className="text-xs text-foreground-400">{t('newPasswordOptional')}</p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? t('loading') : t('saveChanges')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
